  import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, message, Space, Modal, Form, Input, Popconfirm, Upload, Breadcrumb, Select, Descriptions, Avatar, Spin, Progress } from 'antd';
import { UploadOutlined, PlusOutlined, DownloadOutlined, HomeOutlined, SearchOutlined, DeleteOutlined, UserOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../api';
import StudentForm from '../components/StudentForm';

const { Option } = Select;

const StudentManagementPage = () => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isResetPasswordModalVisible, setIsResetPasswordModalVisible] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isBatchEditModalVisible, setIsBatchEditModalVisible] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [studentDetail, setStudentDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Search and Filter State
  const [searchText, setSearchText] = useState('');
  const [classFilter, setClassFilter] = useState(null);
  const [majorFilter, setMajorFilter] = useState(null);
  const [classModeFilter, setClassModeFilter] = useState(null);

  const [form] = Form.useForm();
  
  // 获取当前用户角色
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const userRole = userInfo.role || '';
  const isAdmin = userRole === 'ADMIN' || userRole === 'TEACHING_ADMIN';

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users/students');
      // Ensure response.data is an array before setting state
      if (Array.isArray(response.data)) {
        setStudents(response.data);
      } else {
        setStudents([]); // Default to empty array if data is invalid
        console.error("Expected array from /users/students but got:", response.data);
      }
    } catch (error) {
      message.error('获取学员列表失败');
      setStudents([]); // Default to empty array on error
    }
    setLoading(false);
  };

  const fetchClasses = async () => {
    try {
      // Use /classes/manageable so both ADMIN and CLASS_TEACHER can access it
      const response = await api.get('/classes/manageable');
      setClasses(response.data || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchClasses();
  }, []);

  // Compute unique majors for the filter dropdown
  const uniqueMajors = useMemo(() => {
    // Defensive check: ensure students is a valid array
    if (!students || !Array.isArray(students)) return [];
    try {
      const majors = students
        .filter(s => s && s.major) // Check s exists before accessing major
        .map(s => s.major)
        .filter(m => m && m.trim() !== ''); // Filter out empty or null majors
      return [...new Set(majors)]; // Unique values
    } catch (e) {
      console.error("Error computing unique majors:", e);
      return [];
    }
  }, [students]);

  // Filter Logic
  const filteredStudents = useMemo(() => {
    // Defensive check: ensure students is a valid array
    if (!students || !Array.isArray(students)) return [];
    try {
      return students.filter(student => {
        // Defensive check: skip if student object is null/undefined
        if (!student) return false;
        
        // Use optional chaining and default empty strings to prevent crashes
        const name = student.name || '';
        const account = student.account || '';
        const searchTextLower = (searchText || '').toLowerCase();
        
        const matchesSearch = 
          name.toLowerCase().includes(searchTextLower) ||
          account.toLowerCase().includes(searchTextLower);
        
        // Ensure student.classId is handled as a string for comparison
        const matchesClass = classFilter ? String(student.classId) === String(classFilter) : true;
        const matchesMajor = majorFilter ? student.major === majorFilter : true;
        const matchesClassMode = classModeFilter ? student.classMode === classModeFilter : true;
        
        return matchesSearch && matchesClass && matchesMajor && matchesClassMode;
      });
    } catch (e) {
      console.error("Error filtering students:", e);
      return [];
    }
  }, [students, searchText, classFilter, majorFilter, classModeFilter]);

  const handleBatchEdit = async () => {
    try {
        const values = await form.validateFields();
        const updatePayloads = selectedRowKeys.map(id => {
            const student = students.find(s => s.id === id);
            return {
                id: student.id,
                name: values.name || student.name, // Only update if value provided
                account: values.account || student.account,
                role: 'STUDENT',
                classId: values.classId || student.classId,
                major: values.major || student.major,
                school: values.school || student.school,
                building: values.building || student.building,
                dormitoryNumber: values.dormitoryNumber || student.dormitoryNumber,
                bedNumber: values.bedNumber || student.bedNumber,
                classMode: values.classMode || student.classMode
            };
        });

        await api.post('/users/batch-update', updatePayloads);
        message.success(`成功更新 ${selectedRowKeys.length} 名学员信息`);
        setIsBatchEditModalVisible(false);
        setSelectedRowKeys([]);
        fetchStudents();
        form.resetFields();
    } catch (error) {
        console.error('批量更新失败:', error);
        const errorMessage = error.response?.data?.message || '批量更新失败，请重试';
        message.error(errorMessage);
    }
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await api.post('/users', values);
      message.success('学员创建成功');
      setIsCreateModalVisible(false);
      fetchStudents();
      form.resetFields();
    } catch (error) {
      console.error('创建学员失败:', error);
      const errorMessage = error.response?.data?.message || '创建失败，请重试';
      message.error(errorMessage);
    }
  };

  const [importProgress, setImportProgress] = useState({ visible: false, percent: 0, status: 'active' });

  const handleBatchImport = async (file) => {
    try {
      setImportProgress({ visible: true, percent: 0, status: 'active' });
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/users/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 50) / progressEvent.total);
          setImportProgress({ visible: true, percent, status: 'active' });
        }
      });
      
      // 上传完成，处理中
      setImportProgress({ visible: true, percent: 80, status: 'active' });
      
      // 短暂延迟显示处理完成
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (response.data.errors && response.data.errors.length > 0) {
        setImportProgress({ visible: true, percent: 100, status: 'exception' });
        Modal.warning({
          title: '导入结果反馈',
          content: (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <p>处理成功: {response.data.successCount} / {response.data.totalCount}</p>
              <p>新增: {response.data.createdCount || 0} 人，更新: {response.data.updatedCount || 0} 人</p>
              <p>以下条目导入失败:</p>
              <ul>
                {response.data.errors.map((err, idx) => (
                  <li key={idx} style={{ color: '#ff4d4f' }}>{err}</li>
                ))}
              </ul>
            </div>
          ),
          width: 600
        });
      } else {
        setImportProgress({ visible: true, percent: 100, status: 'success' });
        message.success(`导入完成：新增 ${response.data.createdCount || 0} 人，更新 ${response.data.updatedCount || 0} 人`);
      }
      
      fetchStudents();
      fetchClasses();
      
      // 2秒后隐藏进度条
      setTimeout(() => setImportProgress({ visible: false, percent: 0, status: 'active' }), 2000);
    } catch (error) {
      console.error("Batch import error:", error);
      setImportProgress({ visible: true, percent: 100, status: 'exception' });
      message.error('批量导入失败，请检查文件格式或网络连接');
      setTimeout(() => setImportProgress({ visible: false, percent: 0, status: 'active' }), 2000);
    }
    return false;
  };

  const showEditModal = (student) => {
    setSelectedStudent(student);
    
    // Try to resolve classId from className if classId is missing
    let resolvedClassId = student.classId;
    if (!resolvedClassId && student.className) {
        const matchedClass = classes.find(c => c.name === student.className);
        if (matchedClass) {
            resolvedClassId = matchedClass.id;
        }
    }

    form.setFieldsValue({
        ...student,
        classId: resolvedClassId // Use resolved ID for the Select component
    });
    setIsEditModalVisible(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();
      const { password, ...rest } = values;
      // 确保包含 role 字段和 account
      const payload = {
        ...rest,
        role: 'STUDENT'
      };
      console.log('更新学员，payload:', payload);
      await api.put(`/users/${selectedStudent.id}`, payload);
      message.success('学员信息更新成功');
      setIsEditModalVisible(false);
      fetchStudents();
      form.resetFields();
    } catch (error) {
      console.error('更新学员失败:', error);
      // 改进错误消息提取逻辑
      let errorMessage = '更新失败，请重试';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      message.error(`更新学员失败: ${errorMessage}`);
    }
  };

  const showResetPasswordModal = (student) => {
    setSelectedStudent(student);
    setIsResetPasswordModalVisible(true);
    setNewPassword("");
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      message.error("请输入新密码");
      return;
    }
    try {
      await api.post(`/users/${selectedStudent.id}/reset-password`, { newPassword });
      message.success('密码重置成功');
      setIsResetPasswordModalVisible(false);
    } catch (error) {
      console.error('重置密码失败:', error);
      const errorMessage = error.response?.data?.message || '密码重置失败，请重试';
      message.error(errorMessage);
    }
  };

  const handleDeleteStudent = async (studentId) => {
    try {
      await api.delete(`/users/${studentId}`);
      message.success('学员删除成功');
      fetchStudents();
    } catch (error) {
      console.error('删除学员失败:', error);
      const errorMessage = error.response?.data?.message || '删除失败，请重试';
      message.error(errorMessage);
    }
  };

  const handleDeleteAllStudents = async () => {
    try {
      console.log('开始清空所有学员...');
      await api.delete('/users/students/all');
      message.success('所有学员已清空');
      fetchStudents();
    } catch (error) {
      console.error('清空学员失败:', error);
      const errorMessage = error.response?.data?.message || '清空失败，请重试';
      message.error(errorMessage);
    }
  };
  
  const handleUnbindWechat = async (studentId) => {
    try {
      await api.post(`/users/${studentId}/unbind-wechat`);
      message.success('解绑成功，学员需要重新绑定');
      fetchStudents();
      // 如果详情弹窗打开且是当前学员，刷新详情
      if (isDetailModalVisible && studentDetail && studentDetail.id === studentId) {
        showDetailModal(studentId);
      }
    } catch (error) {
      console.error('解绑失败:', error);
      const errorMessage = error.response?.data?.message || '解绑失败，请重试';
      message.error(errorMessage);
    }
  };

  const showDetailModal = async (studentId) => {
    setIsDetailModalVisible(true);
    setDetailLoading(true);
    setStudentDetail(null);
    try {
      const response = await api.get(`/users/${studentId}/detail`);
      setStudentDetail(response.data);
    } catch (error) {
      message.error('获取学员详情失败');
    }
    setDetailLoading(false);
  };

  const columns = [
    { 
      title: '姓名', 
      dataIndex: 'name', 
      key: 'name', 
      width: 120,
      ellipsis: false,
      render: (text) => (
        <div style={{ 
          fontWeight: 600, 
          wordBreak: 'break-all',
          whiteSpace: 'normal',
          lineHeight: '1.5'
        }}>
          {text}
        </div>
      )
    },
    { title: '账号', dataIndex: 'account', key: 'account' },
    { title: '性别', dataIndex: 'gender', key: 'gender', width: 80, render: value => value || '未设置' },
    { 
      title: '班级', 
      dataIndex: 'classId', 
      key: 'classId',
      render: (classId) => {
        const cls = classes.find(c => String(c.id) === String(classId));
        return cls ? cls.name : classId;
      }
    },
    { title: '专业', dataIndex: 'major', key: 'major' },
    { title: '学校', dataIndex: 'school', key: 'school' },
    { title: '楼栋', dataIndex: 'building', key: 'building' },
    { title: '寝室号', dataIndex: 'dormitoryNumber', key: 'dormitoryNumber' },
    { title: '床位号', dataIndex: 'bedNumber', key: 'bedNumber' },
    { title: '上课方式', dataIndex: 'classMode', key: 'classMode' },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => showDetailModal(record.id)}>详情</Button>
          <Button type="link" size="small" onClick={() => showEditModal(record)}>编辑</Button>
          <Button type="link" size="small" onClick={() => showResetPasswordModal(record)}>重置密码</Button>
          <Popconfirm title="确认解绑该学员吗？解绑后学员需要重新绑定微信和设备才能使用。" onConfirm={() => handleUnbindWechat(record.id)} okText="确认" cancelText="取消">
            <Button type="link" size="small">解绑</Button>
          </Popconfirm>
          {isAdmin && (
            <Popconfirm title="确认删除该学员吗？" onConfirm={() => handleDeleteStudent(record.id)} okText="确认" cancelText="取消">
              <Button type="link" danger size="small">删除</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const studentRowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      'aria-label': `选择学员 ${record.name || ''}`,
    }),
  };

  return (
    <div>
      <div className="page-header">
        <Breadcrumb items={[
          { title: <><HomeOutlined /> 首页</> },
          { title: '用户管理' },
          { title: '学员管理' },
        ]} />
        <h2 className="page-title">学员管理</h2>
      </div>

      <div className="table-card">
        {/* Toolbar with Search and Filter */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <Space size="middle">
              <Input 
                placeholder="搜索姓名或账号" 
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                style={{ width: 200 }}
                allowClear
              />
              <Select
                placeholder="筛选班级"
                style={{ width: 150 }}
                allowClear
                onChange={value => setClassFilter(value)}
                value={classFilter}
                aria-label="筛选班级"
              >
                {classes.map(cls => (
                  <Option key={cls.id} value={cls.id}>{cls.name}</Option>
                ))}
              </Select>
              <Select
                placeholder="筛选专业"
                style={{ width: 150 }}
                allowClear
                onChange={value => setMajorFilter(value)}
                value={majorFilter}
                aria-label="筛选专业"
              >
                {uniqueMajors.map(major => (
                  <Option key={major} value={major}>{major}</Option>
                ))}
              </Select>
              <Select
                placeholder="筛选上课方式"
                style={{ width: 150 }}
                allowClear
                onChange={value => setClassModeFilter(value)}
                value={classModeFilter}
                aria-label="筛选上课方式"
              >
                <Option value="住宿">住宿</Option>
                <Option value="走读">走读</Option>
              </Select>
            </Space>

            <Space>
                <Button 
                    type="primary" 
                    onClick={() => {
                        form.resetFields();
                        setIsBatchEditModalVisible(true);
                    }}
                    disabled={selectedRowKeys.length === 0}
                >
                    批量编辑
                </Button>
                {isAdmin && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateModalVisible(true)}>
                      新建学员
                  </Button>
                )}
                {isAdmin && (
                  <Upload 
                      beforeUpload={handleBatchImport} 
                      showUploadList={false}
                      accept=".xlsx, .xls"
                  >
                      <Button icon={<UploadOutlined />}>批量导入</Button>
                  </Upload>
                )}
                {isAdmin && (
                  <Button type="text" icon={<DownloadOutlined />} href="/template.xlsx" download="学员导入模板.xlsx">
                      下载模板
                  </Button>
                )}
                {isAdmin && (
                  <Popconfirm 
                    title="危险操作：确认清空所有学员吗？" 
                    description="此操作将永久删除所有学员数据及其关联的设备绑定信息，不可恢复！"
                    onConfirm={handleDeleteAllStudents} 
                    okText="确认清空" 
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                  >
                    <Button danger icon={<DeleteOutlined />}>清空所有学员</Button>
                  </Popconfirm>
                )}
            </Space>
        </div>
        
        {importProgress.visible && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fafafa', borderRadius: 8 }}>
            <div style={{ marginBottom: 8, color: '#666' }}>
              {importProgress.status === 'active' && '正在导入...'}
              {importProgress.status === 'success' && '导入完成！'}
              {importProgress.status === 'exception' && '导入完成（部分失败）'}
            </div>
            <Progress 
              percent={importProgress.percent} 
              status={importProgress.status}
              strokeColor={importProgress.status === 'success' ? '#52c41a' : undefined}
            />
          </div>
        )}
        
        <Table 
            rowSelection={studentRowSelection}
            columns={columns} 
            dataSource={filteredStudents}  
            loading={loading} 
            rowKey="id" 
            pagination={{ 
                defaultPageSize: 10, 
                showTotal: (total) => `共 ${total} 条记录`,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100]
            }}
        />
      </div>
      
      <Modal title="新建学员" open={isCreateModalVisible} onOk={handleCreate} onCancel={() => setIsCreateModalVisible(false)}>
        <StudentForm form={form} classes={classes} />
      </Modal>

      <Modal title="编辑学员" open={isEditModalVisible} onOk={handleUpdate} onCancel={() => setIsEditModalVisible(false)}>
        <StudentForm form={form} isEditMode={true} classes={classes} />
      </Modal>

      <Modal 
        title={`批量编辑 (${selectedRowKeys.length} 位学员)`} 
        open={isBatchEditModalVisible} 
        onOk={handleBatchEdit} 
        onCancel={() => setIsBatchEditModalVisible(false)}
      >
        <Form name="batch_edit_form" form={form} layout="vertical">
            <Form.Item name="classId" label="班级">
                <Select placeholder="不修改请留空" allowClear aria-label="选择班级">
                    {classes.map(cls => (
                        <Option key={cls.id} value={cls.id}>{cls.name}</Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item name="major" label="专业">
                <Input placeholder="不修改请留空" />
            </Form.Item>
            <Form.Item name="school" label="学校">
                <Input placeholder="不修改请留空" />
            </Form.Item>
            <Form.Item name="building" label="楼栋">
                <Input placeholder="不修改请留空" />
            </Form.Item>
            <Form.Item name="dormitoryNumber" label="寝室号">
                <Input placeholder="不修改请留空" />
            </Form.Item>
            <Form.Item name="bedNumber" label="床位号">
                <Input placeholder="不修改请留空" />
            </Form.Item>
            <Form.Item name="classMode" label="上课方式">
                <Select placeholder="不修改请留空" allowClear aria-label="选择上课方式">
                    <Option value="住宿">住宿</Option>
                    <Option value="走读">走读</Option>
                </Select>
            </Form.Item>
            <p style={{ color: '#999' }}>注：仅填写的字段会被更新，留空则保持原值。</p>
        </Form>
      </Modal>

      <Modal title={`重置密码 - ${selectedStudent?.name}`} open={isResetPasswordModalVisible} onOk={handleResetPassword} onCancel={() => setIsResetPasswordModalVisible(false)}>
        <Input.Password 
          placeholder="请输入新的临时密码"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
      </Modal>

      <Modal 
        title="学员详情" 
        open={isDetailModalVisible} 
        onCancel={() => setIsDetailModalVisible(false)}
        footer={
          <Space>
            {studentDetail && studentDetail.wechatOpenid && (
              <Popconfirm 
                title="确认解绑该学员吗？" 
                description="解绑后学员需要重新绑定微信和设备才能使用。"
                onConfirm={() => handleUnbindWechat(studentDetail.id)} 
                okText="确认" 
                cancelText="取消"
              >
                <Button danger>解绑微信</Button>
              </Popconfirm>
            )}
            <Button onClick={() => setIsDetailModalVisible(false)}>关闭</Button>
          </Space>
        }
        width={600}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}><Spin /></div>
        ) : studentDetail ? (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              {studentDetail.wechatAvatar ? (
                <Avatar size={80} src={studentDetail.wechatAvatar} />
              ) : (
                <Avatar size={80} icon={<UserOutlined />} />
              )}
              <h3 style={{ marginTop: 12, marginBottom: 0 }}>{studentDetail.name}</h3>
            </div>
            
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="账号">{studentDetail.account || '-'}</Descriptions.Item>
              <Descriptions.Item label="性别">{studentDetail.gender || '未设置'}</Descriptions.Item>
              <Descriptions.Item label="班级">{studentDetail.className || '-'}</Descriptions.Item>
              <Descriptions.Item label="专业">{studentDetail.major || '-'}</Descriptions.Item>
              <Descriptions.Item label="学校">{studentDetail.school || '-'}</Descriptions.Item>
              <Descriptions.Item label="楼栋">{studentDetail.building || '-'}</Descriptions.Item>
              <Descriptions.Item label="寝室号">{studentDetail.dormitoryNumber || '-'}</Descriptions.Item>
              <Descriptions.Item label="床位号">{studentDetail.bedNumber || '-'}</Descriptions.Item>
              <Descriptions.Item label="上课方式">{studentDetail.classMode || '-'}</Descriptions.Item>
            </Descriptions>

            <h4 style={{ marginTop: 20, marginBottom: 12 }}>微信绑定信息</h4>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="微信昵称">{studentDetail.wechatNickname || '未绑定'}</Descriptions.Item>
              <Descriptions.Item label="微信OpenID">{studentDetail.wechatOpenid || '未绑定'}</Descriptions.Item>
              <Descriptions.Item label="绑定手机号">{studentDetail.phone || '未绑定'}</Descriptions.Item>
            </Descriptions>

            <h4 style={{ marginTop: 20, marginBottom: 12 }}>设备绑定信息</h4>
            {studentDetail.device ? (
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="设备品牌">{studentDetail.device.deviceBrand || '-'}</Descriptions.Item>
                <Descriptions.Item label="设备型号">{studentDetail.device.deviceModel || '-'}</Descriptions.Item>
                <Descriptions.Item label="系统版本">{studentDetail.device.deviceSystem || '-'}</Descriptions.Item>
                <Descriptions.Item label="客户端平台">{studentDetail.device.devicePlatform || '-'}</Descriptions.Item>
                <Descriptions.Item label="屏幕分辨率">{studentDetail.device.screenWidth && studentDetail.device.screenHeight ? `${studentDetail.device.screenWidth} x ${studentDetail.device.screenHeight}` : '-'}</Descriptions.Item>
                <Descriptions.Item label="微信版本">{studentDetail.device.wechatVersion || '-'}</Descriptions.Item>
                <Descriptions.Item label="基础库版本">{studentDetail.device.sdkVersion || '-'}</Descriptions.Item>
                <Descriptions.Item label="首次使用">{studentDetail.device.firstUsedAt ? new Date(studentDetail.device.firstUsedAt).toLocaleString() : '-'}</Descriptions.Item>
                <Descriptions.Item label="最后使用">{studentDetail.device.lastUsedAt ? new Date(studentDetail.device.lastUsedAt).toLocaleString() : '-'}</Descriptions.Item>
              </Descriptions>
            ) : (
              <p style={{ color: '#999' }}>未绑定设备</p>
            )}
          </div>
        ) : (
          <p>暂无数据</p>
        )}
      </Modal>
    </div>
  );
};

export default StudentManagementPage;
